import { Options, REPORT_TYPE, ApiParamsType } from "../index.interface"
import { addEvent, getPageRoute, is } from "./common"
import { installOnce } from "./global-flag"

export const DEFAULT_API_HELPER = [{ rule: /(\w+)\/\d{2,}/g, target: "$1" }]

export const DEFAULT_URL_HELPER = [
  { rule: /\/([a-z\-_]+)?\d{2,20}/g, target: "/$1**" } /* 将所有 path 中的数字变成 * */,
  /\/$/ /* 将末尾/去掉 */,
]

export const DEFAULT_PARSE_RESPONSE = (res: any) => {
  if (!res || typeof res !== "object") return {}
  const success = Number(res.code) === 0 ? 1 : 0
  let msg =
    res.msg || res.message || res.subMsg || res.errorMsg || res.ret || res.errorResponse || ""
  if (typeof msg === "object") {
    msg = msg.msg || msg.message || msg.info || msg.ret || JSON.stringify(msg)
  }
  return { code: res.code, msg, success }
}

/**
 * 所有 HttpRequest 实例共享同一层 fetch/XHR Hook，通过 `_instances` 列表分发数据。
 * 跨 module 实例去重由 `installOnce` 负责，详见 `util/global-flag.ts`。
 */
const _instances: HttpRequest[] = []

export class HttpRequest {
  _options: Partial<Options> = {}

  recordFn: Function = () => {}

  constructor(options: Options, recordFn: Function) {
    this._options = options

    this.recordFn = recordFn

    _instances.push(this)
    installOnce("fetch-hooked", () => this.initFetchHook())
    installOnce("xhr-hooked", () => this.initXhrHook())
  }

  private isOnlyApi(url: string) {
    /**
     * 上报的api要排除资源url，这里判断url是否包含.后缀,同时上报的请求url也不计入上报
     */
    if (url.indexOf(".") > -1) {
      return false
    }

    // 白名单逻辑，如果whiteListApis参数有传，就用白名单逻辑，不用黑名单逻辑
    const whiteListApis = this._options?.whiteListApis

    if (Array.isArray(whiteListApis) && whiteListApis?.length > 0) {
      return whiteListApis?.some((api) => {
        try {
          // 正则
          if (api instanceof RegExp) {
            return api.test(url)
          }
          // 自定义函数
          if (typeof api === "function") {
            return api(url)
          }
          // api是string
          return url.includes(api) || url.split("/").pop()?.includes(api.split("/").pop()!)
        } catch (e) {
          return false
        }
      })
    }

    // 黑名单逻辑
    const isIgnore = this._options?.ignore?.ignoreApis?.some((api) => {
      try {
        // 正则
        if (api instanceof RegExp) {
          return api.test(url)
        }
        // 自定义函数
        if (typeof api === "function") {
          return api(url)
        }
        // api是string
        return url.includes(api) || url.split("/").pop()?.includes(api.split("/").pop()!)
      } catch (e) {
        return false
      }
    })

    if (isIgnore) return false
    if (url.includes(getPageRoute(this._options.url!))) {
      return false
    }

    return true
  }

  /**
   * 初始化 Fetch Hook
   *
   * 拦截全局 fetch 函数，监控所有 Fetch 请求的生命周期。
   * 该方法会：
   * 1. 保存原始的 window.fetch 函数
   * 2. 重写 fetch 函数以捕获请求信息
   * 3. 解析请求 URL（支持 string、URL、Request 类型）
   * 4. 收集请求路径、耗时、HTTP状态码、requestId等信息
   * 5. 将收集到的数据广播给所有已注册的实例
   *
   * @private
   * @remarks
   * - 只在全局执行一次，结果广播给所有实例
   * - 通过 response.clone() 避免消费原始响应流
   * - 所有实例共享同一个 Hook，通过 _instances 列表分发数据
   * - 使用 getPageRoute 规范化 API 路径（移除协议、查询参数和数字ID）
   */
  private initFetchHook() {
    const __oFetch__: any = window.fetch

    window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
      if (input === undefined) {
        throw new Error("fetch params can not be empty")
      }

      let api

      if (typeof input === "string") {
        api = input
      } else if (is<URL>(input, "href")) {
        api = input.href
      } else if (is<Request>(input, "url")) {
        api = input.url
      }

      // 移除协议和查询参数，去掉api中的数字（id等）
      api = getPageRoute(api)

      // 上传数据的基本字段，其他字段在index中补充
      const obj: ApiParamsType = {
        path: api,
        start: +new Date(),
        duration: 0,
        httpStatus: 0,
      }

      return __oFetch__
        .apply(window, [input, { ...init }])
        .then(async (response: Response) => {
          obj.duration = getValidDuration(+new Date() - obj.start)
          obj.timestamp = +new Date()
          obj.httpStatus = response.status
          if (response?.headers?.get("content-type")?.includes("application/json")) {
            const consumeResponse = response.clone()
            const result = await consumeResponse.json()
            obj.requestId = result.request_id
          }
          return response
        })
        .catch((err) => {
          obj.duration = +new Date() - obj.start
          obj.httpStatus = 0
          throw err
        })
        .finally(() => {
          delete obj.start
          // 广播给所有已注册的实例，由各实例自行判断是否上报
          _instances.forEach((instance) => {
            instance.isOnlyApi(obj.path) && instance.updateApiList(obj)
          })
        })
    }
  }

  /**
   * 初始化 XMLHttpRequest Hook
   *
   * 拦截全局 XMLHttpRequest 对象，监控所有 XHR 请求的生命周期。
   * 该方法会：
   * 1. 保存原始的 XMLHttpRequest 构造函数
   * 2. 重写 open 和 send 方法以捕获请求信息
   * 3. 监听 readystatechange 事件以获取响应数据
   * 4. 收集请求路径、耗时、HTTP状态码、requestId等信息
   * 5. 将收集到的数据广播给所有已注册的实例
   *
   * @private
   * @remarks
   * - 只在全局执行一次，通过 _xhrHooked 标志控制
   * - 保持原型链完整性，确保 instanceof 检测正常
   * - 所有实例共享同一个 Hook，通过 _instances 列表分发数据
   */
  private initXhrHook() {
    const __oXMLHttpRequest__ = window.XMLHttpRequest
    // @ts-ignore
    window.XMLHttpRequest = function () {
      const xhr: XMLHttpRequest = new __oXMLHttpRequest__()
      const __open__ = xhr.open
      const __send__ = xhr.send

      // 上传数据的基本字段，其他字段在index中补充
      const obj: ApiParamsType = {
        path: "",
        start: +new Date(),
        duration: 0,
        httpStatus: 0,
      }

      xhr.open = (method, url, async = true) => {
        // 移除协议和查询参数，去掉api中的数字（id等）
        if (typeof url === "string") {
          obj.path = getPageRoute(url)
        } else {
          obj.path = getPageRoute(url.href)
        }
        __open__.apply(xhr, [method, url, async])
      }

      xhr.send = (data) => {
        obj.start = Date.now()
        __send__.apply(xhr, [data])
      }

      addEvent(xhr, "readystatechange", () => {
        if (xhr.readyState === 4) {
          // 以状态码在 200~299 外为网络异常
          obj.duration = getValidDuration(+new Date() - obj.start)
          obj.timestamp = +new Date()
          obj.httpStatus = xhr.status
          try {
            obj.requestId = getRequestId(xhr.response)
          } catch (error) {
            obj.requestId = ""
          }
          delete obj.start
          // 广播给所有已注册的实例，由各实例自行判断是否上报
          _instances.forEach((instance) => {
            instance.isOnlyApi(obj.path) && instance.updateApiList(obj)
          })
        }
      })
      return xhr
    }

    // 保持原型链，确保 instanceof XMLHttpRequest 等检测正常
    window.XMLHttpRequest.prototype = __oXMLHttpRequest__.prototype
  }

  public updateApiList(data: ApiParamsType) {
    this.recordFn({
      type: REPORT_TYPE.Api,
      data,
    })
  }

  public destroy() {
    const index = _instances.indexOf(this)
    if (index !== -1) {
      _instances.splice(index, 1)
    }
  }
}

// 解析 response string，获取 request id
function getRequestId(data: string) {
  if (data.indexOf("request_id") > -1) {
    const keyValueAry = data.split(",")
    const requestIdValue = keyValueAry.filter((key) => key.indexOf("request_id") > -1)[0]
    const result = requestIdValue.split(":")[1]
    return result
  }
}

// 耗时超过50s，以50s上报
function getValidDuration(duration: number) {
  if (duration > 50000) {
    return 50000
  }
  return duration
}

/**
 * 工厂函数：为每个 Tracer 实例创建独立的 MyApi 实例并注册。
 * XHR/Fetch Hook 全局只安装一次，数据通过 _instances 广播给所有实例。
 */
export function createApi(options: Options, recordFn: Function): HttpRequest {
  return new HttpRequest(options, recordFn)
}
